import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { KTSVG, toAbsoluteUrl, freshProductTitle } from '@helpers/index';
import { Widget } from '@components/index';
import { getOrderScheduleSelector } from '@modules/order-schedule/core/selectors';
import { getCurrencyCodeSelector } from '@modules/subscriptions/core/selectors';
import { getItemDiscountsByVariantSelector } from '@modules/subscriptions/core/selectors';
import { findFreshLine } from '@modules/subscriptions/utils/lineItems';
import { formatDiscountTitle } from '@modules/subscriptions/core/discountLabel';

export type SingleOrderSummaryProps = {
  order: any;
};

// Collects the item-scoped discounts (Pfotenpunkte, Sommer Aktion) attached to
// the line items of a single scheduled order, de-duplicated. One-time upsells
// added while the current order is in fulfilment ride along on an upcoming
// order, so their discounts surface here rather than on the current order.
const orderItemDiscounts = (
  lines: ILineItem[] | undefined | null,
  discountsByVariant: Record<string, Discount[]>,
): Discount[] => {
  if (!lines?.length) return [];

  const seen = new Set<string>();
  const result: Discount[] = [];

  for (const line of lines) {
    const variantDiscounts = discountsByVariant[String(line?.variantShopifyId)];
    if (!variantDiscounts) continue;
    for (const discount of variantDiscounts) {
      const key = discount.shopifyId ?? discount.title ?? '';
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(discount);
    }
  }

  return result;
};

export const UpcomingOrders = ({ order }: SingleOrderSummaryProps) => {
  const intl = useIntl();
  const lineItem = findFreshLine(order?.lines);
  const orders = useSelector(getOrderScheduleSelector);
  const currencyCode = useSelector(getCurrencyCodeSelector);
  const itemDiscountsByVariant = useSelector(getItemDiscountsByVariantSelector);
  const visibleOrders = orders.slice(0, 4);

  if (!orders.length) {
    return (
      <Widget
        header={
          <div className="upcoming-orders__header">
            <FormattedMessage
              id="ORDERS.UPCOMING.HEADING"
              values={{
                plain: (chunks) => <span>{chunks}</span>,
                strong: (chunks) => <strong>{chunks}</strong>,
              }}
            />
          </div>
        }>
        <div className="upcoming-orders__empty">
          <FormattedMessage id="ORDERS.CURRENT.NONE" />
        </div>
      </Widget>
    );
  }

  return (
    <Widget
      header={
        <div className="upcoming-orders__header">
          <FormattedMessage
            id="ORDERS.UPCOMING.HEADING"
            values={{
              plain: (chunks) => <span>{chunks}</span>,
              strong: (chunks) => <strong>{chunks}</strong>,
            }}
          />
        </div>
      }
      footer={
        <Link to="/orders" className="upcoming-orders__footer-link">
          <FormattedMessage id="ORDERS.MANAGE_LINK" />
          <KTSVG
            path={toAbsoluteUrl('/media/svg/general/link.svg')}
            className="upcoming-orders__footer-icon"
          />
        </Link>
      }
      footerBg="bg-primary-light">
      <div className="upcoming-orders">
        <ul className="upcoming-orders__list">
          {visibleOrders.map((scheduledOrder: any) => (
            <li key={scheduledOrder.id} className="upcoming-orders__item">
              <div className="upcoming-orders__date">
                <span className="upcoming-orders__date-day">
                  <FormattedDate value={new Date(scheduledOrder?.billingDate)} day="2-digit" />
                </span>
                <span className="upcoming-orders__date-month">
                  <FormattedDate value={new Date(scheduledOrder?.billingDate)} month="short" />
                </span>
                <span className="upcoming-orders__date-year">
                  <FormattedDate value={new Date(scheduledOrder?.billingDate)} year="numeric" />
                </span>
              </div>
              <div className="upcoming-orders__body">
                <span className="upcoming-orders__title" title={freshProductTitle(lineItem, intl)}>
                  {freshProductTitle(lineItem, intl)}
                </span>
                {scheduledOrder?.shippingAddress?.address1 && (
                  <span className="upcoming-orders__address">
                    <KTSVG
                      className="upcoming-orders__address-icon"
                      path={toAbsoluteUrl('/media/svg/orders/delivery-rounded.svg')}
                    />
                    {scheduledOrder.shippingAddress.address1}
                    {scheduledOrder.shippingAddress.zip
                      ? `, ${scheduledOrder.shippingAddress.zip}`
                      : ''}
                  </span>
                )}
                {(() => {
                  const discounts = orderItemDiscounts(
                    scheduledOrder?.lines,
                    itemDiscountsByVariant,
                  );
                  if (!discounts.length) return null;
                  return (
                    <div className="upcoming-orders__discounts">
                      {discounts.map((discount) => (
                        <span
                          key={discount.shopifyId ?? discount.title}
                          className="upcoming-orders__discount">
                          {formatDiscountTitle(intl, discount.title, currencyCode)}
                        </span>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </Widget>
  );
};
